import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { ArrowLeft, Search, Calendar, MessageSquare, Briefcase } from "lucide-react";
import styles from "./MyOneOffJobs.module.css"; // NEW CSS
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import { jwtDecode } from 'jwt-decode';
import { getOrCreateGeneralMaintenanceProjectId } from '../../services/GeneralProjectService';
import Dropdown from '../../components/Dropdown/Dropdown';

const MyOneOffJobs = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [oneOffJobs, setOneOffJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userType, setUserType] = useState('HomeUser');

  // Filter & Sort State
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'descending' });
  const [statusFilter, setStatusFilter] = useState("default");

  // --- Initialization ---
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUsername(decodedToken.UserName || 'User');
        setCurrentUserId(decodedToken.nameid);

        // Determine User Type
        const myType = localStorage.getItem('userType');
        if (myType === "Business") {
             setUserType('BusinessUser');
        } else {
            setUserType('HomeUser');
        }

      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, []);

  // --- Fetch Data ---
  useEffect(() => {
    if (currentUserId) {
      const loadJobs = async () => {
        setIsLoading(true);
        try {
          let allJobs = [];
          let soloProjId = -1;

          if (userType === 'BusinessUser') {
              const jobsRes = await axios.get(`${API_BASE_URL}/api/Jobs/getmyactivejobsForBusiness?businessUserId=${currentUserId}`);
              allJobs = jobsRes.data || [];
              soloProjId = await getOrCreateGeneralMaintenanceProjectId(currentUserId, "Business");
          } else {
              const jobsRes = await axios.get(`${API_BASE_URL}/api/Jobs/getmyactivejobs?homeUserId=${currentUserId}`);
              allJobs = jobsRes.data || [];
              soloProjId = await getOrCreateGeneralMaintenanceProjectId(currentUserId);
          }

          const soloJobs = allJobs.filter(j => {
              const jProjId = j.projectId || j.ProjectId;
              return jProjId === soloProjId || !jProjId || jProjId === -1;
          });
          
          // Sort by CreatedAt descending (Newest first) by default
          soloJobs.sort((a, b) => {
             const dateA = new Date(a.created_at || a.CreatedAt || 0);
             const dateB = new Date(b.created_at || b.CreatedAt || 0);
             return dateB - dateA;
          });

          setOneOffJobs(soloJobs);
          setFilteredJobs(soloJobs); 
        } catch (err) {
          console.error("Error loading jobs:", err);
        } finally {
          setIsLoading(false);
        }
      };
      loadJobs();
    }
  }, [currentUserId, userType]);

  // --- Filter & Sort Logic ---
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    let itemsToFilter = [...oneOffJobs];

    if (statusFilter !== "default" && statusFilter !== "All") {
        itemsToFilter = itemsToFilter.filter(job => {
            const status = job.jobStatus || job.JobStatus || job.status || job.Status;
            return status === statusFilter;
        });
    }

    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        itemsToFilter = itemsToFilter.filter(job => {
            const name = job.name || job.Name || '';
            const desc = job.description || job.Description || '';
            return name.toLowerCase().includes(lower) || desc.toLowerCase().includes(lower);
        });
    }

    if (sortConfig.key !== null) {
        itemsToFilter.sort((a, b) => {
            let valA = a[sortConfig.key] || a[sortConfig.key.charAt(0).toUpperCase() + sortConfig.key.slice(1)];
            let valB = b[sortConfig.key] || b[sortConfig.key.charAt(0).toUpperCase() + sortConfig.key.slice(1)];

            if (sortConfig.key === 'created_at') { 
                valA = new Date(valA || 0); 
                valB = new Date(valB || 0); 
            } else if (sortConfig.key === 'quoteCount') {
                valA = Number(valA || 0);
                valB = Number(valB || 0);
            }

            if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
    }
    setFilteredJobs(itemsToFilter);
  }, [oneOffJobs, searchTerm, sortConfig, statusFilter]);

  const NavigateBack = () => {
        const myType = localStorage.getItem('userType');
        if (myType === "Home" || myType === "HomeUser") {
            navigate('/newhome'); 
        } else {
            navigate('/newbusiness'); 
        }
  }

  // Helper for Status Color
  const getStatusClass = (status) => {
      if (status === 'Open') return styles.statusOpen;
      if (status === 'Assigned' || status === 'In Progress') return styles.statusAssigned;
      if (status === 'Completed') return styles.statusCompleted;
      return styles.statusOpen;
  }

  return (
    <div className={styles.pageContainer}>
      
      <ModernNavbarPureCSS UserName={username} prefix={''} />

      {/* GOLD HEADER BAR */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <button onClick={NavigateBack} className={styles.btnGhost}>
              <ArrowLeft size={24} />
            </button>
            <h1 className={styles.headerTitle}>My One-off Jobs</h1>
          </div>
        </div>
      </header>

      <div className={styles.mainContainer}>
        
        {/* FILTER TOOLBAR */}
        <div className={styles.filterToolbar}>
            <div className={styles.searchInputWrapper}>
                <Search className={styles.searchIcon} />
                <input 
                    type="text" 
                    placeholder="Search jobs..." 
                    className={styles.searchInput}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            
            <div className={styles.filterActions}>
                <Dropdown
                    placeholder="Status"
                    options={[
                      { value: "default", label: "Active" },
                      { value: "All", label: "All" },
                      { value: "Open", label: "Open" },
                      { value: "Assigned", label: "Assigned" },
                      { value: "Completed", label: "Completed" },
                    ]}
                    onSelect={setStatusFilter}
                />
                
                <button 
                    className={`${styles.filterBtn} ${sortConfig.key === 'created_at' ? styles.activeSort : ''}`} 
                    onClick={() => handleSort('created_at')}
                >
                    <Calendar size={16} /> Date
                </button>
                
                <button 
                    className={`${styles.filterBtn} ${sortConfig.key === 'quoteCount' ? styles.activeSort : ''}`} 
                    onClick={() => handleSort('quoteCount')}
                >
                    <MessageSquare size={16} /> Quotes
                </button>
            </div>
        </div>

        {/* JOB LIST */}
        <div className={styles.jobListContainer}>
            {isLoading && <p style={{padding:'2rem', textAlign:'center'}}>Loading jobs...</p>}
            
            {!isLoading && filteredJobs.length === 0 && (
                <div className={styles.emptyState}>
                    <Briefcase size={48} style={{opacity:0.2, marginBottom:'1rem'}} />
                    <p>No jobs found.</p>
                    {oneOffJobs.length === 0 && (
                        <button 
                            className={styles.btnPrimary} 
                            style={{marginTop:'1rem'}}
                            onClick={() => navigate('/newhome')}
                        >
                            Back to Dashboard
                        </button>
                    )}
                </div>
            )}

            {!isLoading && filteredJobs.length > 0 && filteredJobs.map(job => {
                const jobId = job.job_id || job.JobId || job.id || job.Id;
                const jobName = job.name || job.Name || 'Untitled Job';
                const jobStatus = job.jobStatus || job.JobStatus || job.status || job.Status;
                const jobDate = job.created_at || job.CreatedAt;
                const quotes = job.quoteCount || job.QuoteCount || 0;

                return (
                  <div key={jobId} className={styles.jobCard}>
                    <div className={styles.jobInfo}>
                      <h4>{jobName}</h4>
                      <div className={styles.jobMeta}>
                        <span className={getStatusClass(jobStatus)}>{jobStatus}</span>
                        <span>{jobDate ? new Date(jobDate).toLocaleDateString() : "N/A"}</span>
                      </div>
                    </div>
                    <div className={styles.jobActions}>
                      <div className={styles.quoteCount}>
                        {quotes} {quotes === 1 ? 'Quote' : 'Quotes'}
                      </div>
                      <button className={styles.btnManage} onClick={() => navigate(`/job-console/${jobId}`)}>
                          Manage
                      </button>
                    </div>
                  </div>
                );
            })}
        </div>

      </div>
    </div>
  );
};

export default MyOneOffJobs;